import React, { useEffect, forwardRef, useImperativeHandle, useRef, useMemo, useState } from 'react';
import { Splide, SplideSlide } from '@splidejs/react-splide';

import ProductTile from '@src/stories/ecommerce/Product/ProductTile';

import useObviyoRecommendations from '@src/hooks/useObviyoRecommendations';
import { useResponsiveBreakpoints, getGlobalBreakpoints } from '@src/hooks/useMediaQuery';

import shopifyHelpers from 'frontend/js/shopify';

interface Props {
  settings: any;
  globalSettings?: any;
  onRecommendationsLoaded?: () => void; // Optional callback for when recommendations are loaded
  type?: 'block' | 'section';
}

interface BreakpointConfig {
  perPage?: number;
  perMove?: number;
  gap?: number;
  padding?: {
    left?: number;
    right?: number;
  };
}

export const ObviyoProductRecommendations = forwardRef<any, Props>(
  ({ settings, onRecommendationsLoaded, type }, ref) => {
    const {
      product_list,
      recommendation_id,
      max_results,
      show_price = true,
      show_tags = false,
      show_quickadd = false,
      enable_wishlist = false,
      tile_type = 'default',

      // Splide settings (no defaults here to preserve undefined checks)
      slide_type = 'slide',
      wrap_around,
      slides_per_page,
      slides_per_page_ultrawide,
      slides_per_page_desktop,
      slides_per_page_tablet,
      slides_per_page_mobile,
      slides_to_scroll,
      slides_to_scroll_ultrawide,
      slides_to_scroll_desktop,
      slides_to_scroll_tablet,
      slides_to_scroll_mobile,
      slide_gap,
      slide_gap_ultrawide,
      slide_gap_desktop,
      slide_gap_tablet,
      slide_gap_mobile,
      carousel_padding_left,
      carousel_padding_left_ultrawide,
      carousel_padding_left_desktop,
      carousel_padding_left_tablet,
      carousel_padding_left_mobile,
      carousel_padding_right,
      carousel_padding_right_ultrawide,
      carousel_padding_right_desktop,
      carousel_padding_right_tablet,
      carousel_padding_right_mobile,
      show_navigation = false,
      use_external_arrows = false,
      show_pagination = false,
      show_pagination_mobile = false,
      show_pagination_tablet = false,
      show_pagination_ultrawide = false,
      pagination_style = 'dots',
      lazy_load_strategy = 'nearby',
      draggable = true,
      drag_mode = 'snap',
      snap_enabled = true,
      transition_speed = 400
    } = settings || {};

    // Use global breakpoints for consistency
    const globalBreakpoints = getGlobalBreakpoints();
    const { mobile: mobile_max, tablet: tablet_max, desktop: desktop_max } = globalBreakpoints;

    // State for converted product list
    const [convertedProductList, setConvertedProductList] = useState(null);
    const [isConvertingProductList, setIsConvertingProductList] = useState(false);
    const [productListError, setProductListError] = useState(null);

    // Use product_list if available, otherwise fall back to Obviyo recommendations
    const useProductList = product_list && Array.isArray(product_list) && product_list.length > 0;

    const {
      isReady,
      loading,
      error,
      recommendations: obviyoRecommendations
    } = useObviyoRecommendations(useProductList ? undefined : recommendation_id, {
      numResults: max_results
    });

    // Use converted product_list if available, otherwise use Obviyo recommendations
    // Apply max_results limit to product_list if specified
    const recommendations =
      useProductList && convertedProductList
        ? max_results
          ? convertedProductList.slice(0, max_results)
          : convertedProductList
        : obviyoRecommendations;
    const splideRef = useRef<Splide | null>(null);

    // Use responsive breakpoints hook
    const { isMobile, isTablet, isDesktop, isUltrawide } = useResponsiveBreakpoints();

    // Convert product_list using getShopifyProductList when it changes
    useEffect(() => {
      if (useProductList) {
        setIsConvertingProductList(true);
        setProductListError(null);

        // Extract product IDs from the product_list
        const productIds = product_list.map((product) => product.id);

        shopifyHelpers
          .getShopifyProductList(productIds)
          .then((transformedProducts) => {
            setConvertedProductList(transformedProducts);
            setIsConvertingProductList(false);
          })
          .catch((error) => {
            console.error('Error converting product_list:', error);
            setProductListError(error);
            setIsConvertingProductList(false);
            // Fallback to original product_list if conversion fails
            setConvertedProductList(product_list);
          });
      } else {
        setConvertedProductList(null);
        setIsConvertingProductList(false);
        setProductListError(null);
      }
    }, [useProductList, product_list]);

    // Build breakpoints object only if responsive settings are provided
    const buildBreakpoints = () => {
      const breakpoints = {};

      // Helper function to check if any mobile settings are defined and different
      const hasMobileSettings = () => {
        return (
          slides_per_page_mobile !== undefined ||
          slides_to_scroll_mobile !== undefined ||
          slide_gap_mobile !== undefined ||
          carousel_padding_left_mobile !== undefined ||
          carousel_padding_right_mobile !== undefined
        );
      };

      if (hasMobileSettings()) {
        const mobileConfig: BreakpointConfig = {};
        if (slides_per_page_mobile !== undefined) mobileConfig.perPage = slides_per_page_mobile;
        if (slides_to_scroll_mobile !== undefined) mobileConfig.perMove = slides_to_scroll_mobile;
        if (slide_gap_mobile !== undefined) mobileConfig.gap = slide_gap_mobile;

        const mobilePadding: BreakpointConfig['padding'] = {};
        if (carousel_padding_left_mobile !== undefined) mobilePadding.left = carousel_padding_left_mobile;
        if (carousel_padding_right_mobile !== undefined) mobilePadding.right = carousel_padding_right_mobile;
        if (Object.keys(mobilePadding).length > 0) mobileConfig.padding = mobilePadding;

        breakpoints[mobile_max] = mobileConfig;
      }

      // Helper function to check if any tablet settings are defined and different
      const hasTabletSettings = () => {
        return (
          slides_per_page_tablet !== undefined ||
          slides_to_scroll_tablet !== undefined ||
          slide_gap_tablet !== undefined ||
          carousel_padding_left_tablet !== undefined ||
          carousel_padding_right_tablet !== undefined
        );
      };

      if (hasTabletSettings()) {
        const tabletConfig: BreakpointConfig = {};
        if (slides_per_page_tablet !== undefined) tabletConfig.perPage = slides_per_page_tablet;
        if (slides_to_scroll_tablet !== undefined) tabletConfig.perMove = slides_to_scroll_tablet;
        if (slide_gap_tablet !== undefined) tabletConfig.gap = slide_gap_tablet;

        const tabletPadding: BreakpointConfig['padding'] = {};
        if (carousel_padding_left_tablet !== undefined) tabletPadding.left = carousel_padding_left_tablet;
        if (carousel_padding_right_tablet !== undefined) tabletPadding.right = carousel_padding_right_tablet;
        if (Object.keys(tabletPadding).length > 0) tabletConfig.padding = tabletPadding;

        breakpoints[tablet_max] = tabletConfig;
      }

      // Helper function to check if any desktop settings are defined and different
      const hasDesktopSettings = () => {
        return (
          slides_per_page_desktop !== undefined ||
          slides_to_scroll_desktop !== undefined ||
          slide_gap_desktop !== undefined ||
          carousel_padding_left_desktop !== undefined ||
          carousel_padding_right_desktop !== undefined
        );
      };

      if (hasDesktopSettings()) {
        const desktopConfig: BreakpointConfig = {};
        if (slides_per_page_desktop !== undefined) desktopConfig.perPage = slides_per_page_desktop;
        if (slides_to_scroll_desktop !== undefined) desktopConfig.perMove = slides_to_scroll_desktop;
        if (slide_gap_desktop !== undefined) desktopConfig.gap = slide_gap_desktop;

        const desktopPadding: BreakpointConfig['padding'] = {};
        if (carousel_padding_left_desktop !== undefined) desktopPadding.left = carousel_padding_left_desktop;
        if (carousel_padding_right_desktop !== undefined) desktopPadding.right = carousel_padding_right_desktop;
        if (Object.keys(desktopPadding).length > 0) desktopConfig.padding = desktopPadding;

        breakpoints[desktop_max] = desktopConfig;
      }

      return Object.keys(breakpoints).length > 0 ? breakpoints : undefined;
    };

    const shouldShowArrows = useMemo(() => {
      if (!recommendations || recommendations.length === 0) return false;

      const currentPerPage = isUltrawide
        ? slides_per_page_ultrawide || slides_per_page
        : isDesktop
        ? slides_per_page_desktop || slides_per_page
        : isTablet
        ? slides_per_page_tablet || slides_per_page
        : isMobile
        ? slides_per_page_mobile || slides_per_page
        : slides_per_page;

      return currentPerPage < recommendations.length;
    }, [recommendations, isMobile, isTablet, isDesktop, isUltrawide]);

    const isPaginationEnabled = isMobile
      ? show_pagination_mobile
      : isTablet
      ? show_pagination_tablet
      : isUltrawide
      ? show_pagination_ultrawide
      : show_pagination;

    const splideOptions = {
      type: slide_type,
      rewind: wrap_around,
      perPage: slides_per_page_ultrawide || slides_per_page || 4,
      perMove: slides_to_scroll_ultrawide || slides_to_scroll || 1,
      gap: slide_gap_ultrawide || slide_gap || 0,
      padding: {
        left: carousel_padding_left_ultrawide || carousel_padding_left || 0,
        right: carousel_padding_right_ultrawide || carousel_padding_right || 0
      },
      arrows: use_external_arrows ? false : shouldShowArrows && show_navigation,
      pagination: isPaginationEnabled,
      lazyLoad: lazy_load_strategy,
      drag: draggable ? (drag_mode === 'free' ? ('free' as const) : true) : false,
      snap: snap_enabled,
      speed: transition_speed,
      ...(buildBreakpoints() && { breakpoints: buildBreakpoints() })
    };

    // Expose the splide ref to parent components (for CarouselArrows)
    useImperativeHandle(ref, () => ({
      navigateToNext: () => {
        if (!splideRef.current?.splide) return;
        splideRef.current.splide.go('>');
      },
      navigateToPrev: () => {
        if (!splideRef.current?.splide) return;
        splideRef.current.splide.go('<');
      },
      // Expose the entire splide instance for advanced controls
      splide: splideRef.current?.splide || null
    }));

    useEffect(() => {
      if (recommendations) {
        if (recommendations.length > 0) {
          // Show the entire block if recommendations are loaded
          const obviyoElement = document.querySelector(`[data-obviyo-block="${settings.id}"]`);
          if (obviyoElement) {
            obviyoElement.classList.remove('hidden');
          }
          if (onRecommendationsLoaded) {
            onRecommendationsLoaded(true);
          }
        } else {
          if (onRecommendationsLoaded) {
            onRecommendationsLoaded(false);
          }
        }
        splideRef.current?.splide.refresh();
      }
    }, [recommendations, onRecommendationsLoaded, settings.id]);

    // Handle external arrows if enabled
    useEffect(() => {
      if (!use_external_arrows || !recommendations) return;

      const externalArrowsElements = document.querySelectorAll(
        `[data-carousel-arrows-for="${settings?.carousel_id || settings.id}"]`
      );

      if (externalArrowsElements.length === 0) return;

      const allPrevButtons: Element[] = [];
      const allNextButtons: Element[] = [];

      // Collect all prev and next buttons from all external arrow sets
      externalArrowsElements.forEach((externalArrows) => {
        const prevButton = externalArrows.querySelector('.carousel__arrow--prev');
        const nextButton = externalArrows.querySelector('.carousel__arrow--next');

        if (prevButton) allPrevButtons.push(prevButton);
        if (nextButton) allNextButtons.push(nextButton);
      });

      // Hide arrows if not needed
      if (!shouldShowArrows) {
        allPrevButtons.forEach((button) => button.classList.add('invisible'));
        allNextButtons.forEach((button) => button.classList.add('invisible'));
      }

      const updateArrowStates = () => {
        if (!splideRef.current?.splide) return;

        const splide = splideRef.current.splide;
        const currentPerPage = splide.options.perPage || 1;
        const canGoPrev = splide.index > 0;
        const canGoNext = splide.index + currentPerPage < splide.length;

        allPrevButtons.forEach((prevButton) => {
          if (slide_type === 'slide' && !wrap_around) {
            prevButton.toggleAttribute('disabled', !canGoPrev);
          }
        });

        allNextButtons.forEach((nextButton) => {
          if (slide_type === 'slide' && !wrap_around) {
            nextButton.toggleAttribute('disabled', !canGoNext);
          }
        });
      };

      // Add event listeners to all prev buttons
      allPrevButtons.forEach((prevButton) => {
        prevButton.addEventListener('click', function () {
          splideRef.current?.splide?.go('<');
        });
      });

      // Add event listeners to all next buttons
      allNextButtons.forEach((nextButton) => {
        nextButton.addEventListener('click', function () {
          splideRef.current?.splide?.go('>');
        });
      });

      // Listen for slide changes to update arrow states
      if (splideRef.current?.splide) {
        splideRef.current.splide.on('moved', updateArrowStates);
        splideRef.current.splide.on('mounted', updateArrowStates);

        // Initial state update
        updateArrowStates();
      }

      return () => {
        if (splideRef.current?.splide) {
          splideRef.current.splide.off('moved', updateArrowStates);
          splideRef.current.splide.off('mounted', updateArrowStates);
        }
      };
    }, [use_external_arrows, slide_type, wrap_around, shouldShowArrows, recommendations]);

    // Early return if neither product_list nor recommendation_id is provided
    if (!useProductList && !recommendation_id) {
      console.warn('ObviyoProductRecommendations: Either product_list or recommendation_id is required');
      return null;
    }

    // For product_list: check if converting, has errors, or no recommendations
    // For Obviyo: check if ready, no error, not loading, and has recommendations
    if (useProductList) {
      // Still converting product list or has error
      if (isConvertingProductList || productListError) return null;
      // No recommendations after conversion
      if (!recommendations || recommendations.length === 0) return null;
    } else {
      if (!isReady || error || loading || !recommendations || recommendations.length === 0) return null;
    }

    return (
      <div className={`${isPaginationEnabled && `splide-pagination-${pagination_style}`}`}>
        <Splide
          ref={splideRef}
          options={splideOptions}
          data-carousel-id={settings?.carousel_id || settings.id}
        >
          {recommendations.map((product) => (
            <SplideSlide key={product.id}>
              <div data-obv-recommend={recommendation_id}>
                <ProductTile
                  settings={settings}
                  product={product}
                  siblings={undefined}
                  globals={{}}
                  enableWishlist={enable_wishlist || true}
                  showPrice={show_price || true}
                  showQuickAdd={show_quickadd || true}
                  showTags={show_tags || true}
                  type={tile_type}
                />
              </div>
            </SplideSlide>
          ))}
        </Splide>
      </div>
    );
  }
);
